import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';

/** A hero slide an admin manages; shown when no paid promotion fills the slot. */
export interface HouseSlide {
  promid: number;
  page: string | null;
  pages: string[];
  name: string | null;
  description: string | null;
  tagline: string | null;
  link_ext: string | null;
  asid: number | null;
  image_url: string | null;
  active: number;
  order_by: number;
  date_start: string | null;
  date_expired: string | null;
}

export interface HouseSlideInput {
  page: string;
  name: string;
  description: string;
  link_ext: string;
  asid: number;
  order_by: number;
  date_start: string;
  date_expired: string;
  active: 0 | 1;
}

type ListResponse = { pages: string[]; slides: HouseSlide[] };

const BASE = '/api/admin/promotions/house-slides';
const KEY = ['house-slides'];

// The API wraps payloads in `{ data }`; tolerate a bare payload too.
const unwrap = <T,>(res: unknown): T => ((res as { data?: T })?.data ?? res) as T;

export const useHouseSlidesQuery = () => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: KEY,
    queryFn: withErrorHandling(
      async () => unwrap<ListResponse>(await request(BASE)),
      'Failed to load slider content',
    ),
  });
};

export const useSaveHouseSlideMutation = () => {
  const request = useClientStore((s) => s.request);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: withErrorHandling(
      async ({ promid, input }: { promid?: number; input: HouseSlideInput }) =>
        request(promid ? `${BASE}/${promid}` : BASE, promid ? 'PUT' : 'POST', input),
      'Failed to save slide',
    ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
};

export const useRemoveHouseSlideMutation = () => {
  const request = useClientStore((s) => s.request);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: withErrorHandling(
      async (promid: number) => request(`${BASE}/${promid}`, 'DELETE'),
      'Failed to remove slide',
    ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
};
